import { Component, OnInit, OnDestroy } from '@angular/core';
import { Subject, forkJoin } from 'rxjs';
import { takeUntil, finalize } from 'rxjs/operators';
import { TranslateService } from '@ngx-translate/core';
import { CardAnalyticsPeriod, CardAnalyticsQuery, UserSnapshotResponse, UserDistributionResponse, UserTrendsResponse, UserGeoResponse } from '../../../services/user-analytics/user-analytics.model';
import { UserAnalyticsService } from '../../../services/user-analytics/user-analytics.abstract.service';

@Component({
    selector: 'app-user-analytics',
    templateUrl: './user-analytics.component.html',
    styleUrls: ['./user-analytics.component.scss'],
})
export class UserAnalyticsComponent implements OnInit, OnDestroy {
    private destroy$ = new Subject<void>();

    selectedPeriod: CardAnalyticsPeriod = '1y';
    customFrom: string | null = null;
    customTo: string | null = null;
    showCustomDates = false;

    loading = false;
    error = false;

    snapshot: UserSnapshotResponse | null = null;
    distribution: UserDistributionResponse | null = null;
    trends: UserTrendsResponse | null = null;
    geo: UserGeoResponse | null = null;

    periods: { value: CardAnalyticsPeriod; labelKey: string }[] = [
        { value: '6m', labelKey: 'user_records.analytics.period_6m' },
        { value: '1y', labelKey: 'user_records.analytics.period_1y' },
        { value: '2y', labelKey: 'user_records.analytics.period_2y' },
        { value: 'custom', labelKey: 'user_records.analytics.period_custom' },
    ];

    constructor(
        private analyticsService: UserAnalyticsService,
        private translate: TranslateService,
    ) {}

    ngOnInit(): void {
        this.loadData();
        this.translate.onLangChange.pipe(takeUntil(this.destroy$)).subscribe(() => {
            this.loadData();
        });
    }

    ngOnDestroy(): void {
        this.destroy$.next();
        this.destroy$.complete();
    }

    onPeriodChange(period: CardAnalyticsPeriod): void {
        this.selectedPeriod = period;
        this.showCustomDates = period === 'custom';

        if (period !== 'custom') {
            this.customFrom = null;
            this.customTo = null;
            this.loadData();
        }
    }

    onCustomDateChange(): void {
        if (this.customFrom && this.customTo) {
            this.loadData();
        }
    }

    retry(): void {
        this.loadData();
    }

    loadData(): void {
        if (this.selectedPeriod === 'custom' && (!this.customFrom || !this.customTo)) {
            return;
        }

        const query: CardAnalyticsQuery = {
            period: this.selectedPeriod,
        };

        if (this.selectedPeriod === 'custom') {
            query.from = this.customFrom;
            query.to = this.customTo;
        }

        this.loading = true;
        this.error = false;

        forkJoin({
            snapshot: this.analyticsService.getSnapshot(),
            distribution: this.analyticsService.getDistribution(query),
            trends: this.analyticsService.getTrends(query),
            geo: this.analyticsService.getGeo(),
        })
            .pipe(
                takeUntil(this.destroy$),
                finalize(() => {
                    this.loading = false;
                }),
            )
            .subscribe({
                next: (result) => {
                    this.snapshot = result.snapshot;
                    this.distribution = result.distribution;
                    this.trends = result.trends;
                    this.geo = result.geo;
                },
                error: () => {
                    this.error = true;
                    this.snapshot = null;
                    this.distribution = null;
                    this.trends = null;
                    this.geo = null;
                },
            });
    }
}
